import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../lib/cn';
import { Input, type InputProps } from './input';

export type PasswordInputProps = Omit<InputProps, 'type'> & {
  /** Classes for the positioning wrapper. `className` still goes to the input. */
  wrapperClassName?: string;
};

/**
 * Password field with a show/hide toggle. Drop-in replacement for
 * `<Input type="password">` — `className` still lands on the input itself, so
 * existing call sites (including the storefront's bespoke `inputCls`) swap
 * without touching their styling.
 *
 * The toggle is `tabIndex={-1}` on purpose: revealing a password is a mouse
 * affordance, and putting a button between the password field and the submit
 * button breaks keyboard submit for everyone to serve a rare case.
 *
 * `type` is deliberately not overridable — this component owns it.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, wrapperClassName, disabled, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);

    return (
      <div className={cn('relative', wrapperClassName)}>
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          disabled={disabled}
          // Edge/IE render their own reveal control; ours would sit on top of it.
          className={cn('pr-10 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden', className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
